import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

// colours: [{ name, hex }] or [{ name, image }]
// Boxed card grid. Optionally controlled (activeIndex + onSelect) and/or paginated.
export const ColourSwatches = ({
  colours = [],
  testid = "colour-swatches",
  columns = "grid-cols-3 sm:grid-cols-4 md:grid-cols-5",
  activeIndex,
  onSelect,
  paginate = false,
  perView = 6,
}) => {
  const [internal, setInternal] = useState(0);
  const [start, setStart] = useState(0);
  const active = activeIndex !== undefined ? activeIndex : internal;
  const select = (i) => (onSelect ? onSelect(i) : setInternal(i));

  const paged = paginate && colours.length > perView;
  const maxStart = Math.max(0, colours.length - perView);
  const list = paged ? colours.slice(start, start + perView) : colours;
  const gridCols = paginate ? `grid-cols-3 sm:grid-cols-6` : columns;

  return (
    <div data-testid={testid}>
      {paged && (
        <div className="mb-4 flex items-center justify-end gap-3">
          <span className="mr-auto text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground">
            {start + 1}–{Math.min(start + perView, colours.length)} of {colours.length}
          </span>
          <button
            type="button"
            onClick={() => setStart((s) => Math.max(0, s - perView))}
            disabled={start === 0}
            data-testid={`${testid}-prev`}
            aria-label="Previous swatches"
            className="flex h-10 w-10 items-center justify-center border border-foreground/30 transition-colors hover:bg-foreground hover:text-background disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setStart((s) => Math.min(maxStart, s + perView))}
            disabled={start >= maxStart}
            data-testid={`${testid}-next`}
            aria-label="Next swatches"
            className="flex h-10 w-10 items-center justify-center border border-foreground/30 transition-colors hover:bg-foreground hover:text-background disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className={`grid gap-x-5 gap-y-6 ${gridCols}`}>
        {list.map((c, li) => {
          const i = paged ? start + li : li;
          return (
            <button
              key={`${c.name}-${i}`}
              type="button"
              onClick={() => select(i)}
              data-testid={`${testid}-${i}`}
              className="group text-left"
            >
              <div
                className={`aspect-square w-full overflow-hidden border transition-all duration-300 ${
                  active === i
                    ? "ring-2 ring-foreground ring-offset-2 ring-offset-background border-transparent"
                    : "border-border/60"
                }`}
                style={c.hex && !c.image ? { backgroundColor: c.hex } : undefined}
              >
                {c.image && (
                  <img
                    src={c.image}
                    alt={`${c.name} finish swatch`}
                    className="h-full w-full scale-[1.18] object-cover transition-transform duration-700 group-hover:scale-125"
                    loading="lazy"
                  />
                )}
              </div>
              <span className="mt-2 block text-[0.7rem] uppercase tracking-[0.12em] text-muted-foreground group-hover:text-foreground">
                {c.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const CustomColourPrompt = () => (
  <p className="mt-8 text-sm leading-relaxed text-muted-foreground" data-testid="custom-colour-prompt">
    Not seeing the colour you like?{" "}
    <Link to="/contact" className="border-b border-foreground pb-0.5 text-foreground">
      Contact us for a custom finish →
    </Link>
  </p>
);
